import { legenditemHeight, legendWidth, height, width, setUpChart, selectSvg } from '~/app/d3charts/utils/common'
import { buildXScaleBand, buildYScale } from '~/app/d3charts/utils/chart_scales'
import { buildXAxis, buildYAxis, yAxisUnit } from '~/app/d3charts/utils/chart_axis'

import { color, setupColor } from '~/app/d3charts/stacked_bar_chart/colors'

import * as d3 from 'd3'
import 'd3-scale'
import d3Tip from 'd3-tip'

const tooltipContainerCss = 'd3-tooltip bg-dark rounded-1 p-3'
const innerTooltipCss = 'text-neutral-000 label-1'
const stackPadding = 2
const roundedCornersDegree = 12

export function build(dataSets, containerSelector, xAxis, yAxis, stackedKeys) {
  const stackedKeysLength = stackedKeys.length
  setupColor(stackedKeysLength)

  setUpChart(containerSelector, stackedKeysLength)
  const svg = selectSvg(containerSelector)

  const chart = svg.select('.chart')

  // Header - Legend
  const legend = buildLegend(svg, dataSets, stackedKeys, stackedKeysLength)

  legend.append('circle').attr('r', 7).attr('stroke-width', 3)

  legend
    .append('text')
    .attr('x', 15)
    .attr('y', 7)
    .attr('font-size', '12')
    .text(function (data) {
      return data.replaceAll('_', ' ')
    })

  svg
    .selectAll('circle')
    .attr('fill', function (data, index) {
      return color(index)
    })
    .attr('stroke', function (data, index) {
      return color(index % stackedKeysLength)
    })

  // Chart - Tooltip
  const stackedTooltip = d3Tip()
    .attr('class', tooltipContainerCss)
    .offset([-10, 0])
    .html(function (event, data) {
      const result = stackedKeys.map(key => {
        if (data.data[key]) {
          return `<div><span class='${innerTooltipCss}'> ${key.replaceAll('_', ' ')}: ${data.data[key]} ${yAxisUnit}</span></div>`
        }
      })
      return result.join('')
    })

  // Chart - X axis and grid
  const xScale = buildXScaleBand(xAxis, width(), 0.9)
  buildXAxis(chart, height, xScale)

  // Chart - Y axis
  const yScale = buildYScale(yAxis, height)
  buildYAxis(chart, height, yScale, yAxisUnit)

  // Chart - Bars
  dataSets.forEach((dataSet, index) => {
    buildStackedChart(dataSet, index)
  })

  function buildStackedChart(dataSet, dataSetIndex) {
    const stackGen = d3.stack().keys(stackedKeys)
    const stackedSeries = stackGen(dataSet.data)
    const stacked = chart
      .selectAll(`g.series.${dataSet.name.substring(0, 3)}`)
      .data(stackedSeries)
      .join('g')
      .classed('series', true)
      .attr('fill', function (d, index) {
        return color(dataSetIndex * stackedKeysLength + index)
      })
      .attr('stroke', function (d, index) {
        return color(index)
      })

    stacked
      .selectAll('rect')
      .data(data => data)
      .enter()
      .append('rect')
      .attr('width', xScale.bandwidth())
      .attr('height', data => Math.max(yScale(data[0]) - yScale(data[1]) - stackPadding, 0))
      .attr('rx', roundedCornersDegree) // make bar borders rounded
      .attr('x', data => xScale(data.data.year))
      .attr('y', data => yScale(data[1]) + stackPadding)
      .call(stackedTooltip)
      .on('mouseover', stackedTooltip.show)
      .on('mouseout', stackedTooltip.hide)
  }
}

function buildLegend(svg, dataSets, stackedKeys, stackedKeysLength) {
  const legendGroup = svg.select('.legend-group')

  const itemWidth = legendWidth / dataSets.length

  const legendColumn = legendGroup
    .selectAll(`.legend-column`)
    .data(dataSets)
    .enter()
    .append('g')
    .classed('legend-column', true)
    .attr('transform', function (d, index) {
      return `translate(${index * itemWidth}, 0)`
    })

  legendColumn
    .append('text')
    .text(dataSet => dataSet.name.replaceAll('_', ' '))
    .attr('font-size', '12')

  const legend = legendColumn
    .selectAll('.legend')
    .data(stackedKeys)
    .enter()
    .append('g')
    .attr('class', 'legend')
    .attr('transform', function (d, index) {
      return `translate(0, ${(index % stackedKeysLength) * legenditemHeight + legenditemHeight})`
    })

  return legend
}
